/**
 * **The level each wellbeing signal is judged low against** — the trainer's own, off the two reads
 * that state it (attention-queue R25).
 *
 * `GET /attention-queue/preferences` carries, for the low-sleep and the low-energy reason, the level
 * in force (`threshold`) and the default it replaces (`defaultThreshold`); `GET /vitals/catalog`
 * carries the default again with the reading's type. The order is the server's own: the trainer's
 * level, else the default the preferences name, else the catalogue's, and — where neither read can
 * say, which is both having FAILED — the numbers `wellbeingBands` was written with, which are the
 * same defaults. While the reads are still open nothing is painted by this at all: every screen
 * that draws a band waits for them (`lib/useWellbeingLevels`' `pending`).
 *
 * It is ONE function so that the roster's two columns, the client's Trendy charts, the session
 * table, the plan overview and the task cards cannot disagree about what a short night is.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import type {
  TrainWithMeModulesAttentionDtosResponsesAttentionPreferencesResponse as AttentionPreferencesResponse,
  TrainWithMeModulesVitalsDtosResponsesVitalCatalogResponse as VitalCatalogResponse,
} from '~/shared/api/generated/model';

import { DEFAULT_WELLBEING_LEVELS, type WellbeingLevels, type WellbeingSignal } from './wellbeingBands';

/** The catalogue's name for the reading behind each signal. The app's "mood" column is energy. */
const VITAL_TYPE_OF_SIGNAL: Readonly<Record<WellbeingSignal, string>> = {
  sleep: 'SleepDuration',
  mood: 'EnergyLevel',
};

function level(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function wellbeingLevels(
  preferences: AttentionPreferencesResponse | null | undefined,
  catalog: VitalCatalogResponse | null | undefined,
): WellbeingLevels {
  const of = (signal: WellbeingSignal): number => {
    const vitalType = VITAL_TYPE_OF_SIGNAL[signal];
    const preference = preferences?.items?.find((item) => item.vitalType === vitalType);
    const published = catalog?.types?.find((type) => type.type === vitalType);
    return (
      level(preference?.threshold) ??
      level(preference?.defaultThreshold) ??
      level(published?.defaultLowBelow) ??
      DEFAULT_WELLBEING_LEVELS[signal]
    );
  };
  return { sleep: of('sleep'), mood: of('mood') };
}
